import React from 'react';
import PropTypes from 'prop-types';
import { useHref } from 'react-router-dom-v5-compat';
import {
  VaLink,
  VaLinkAction,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import withRouter from '../../utils/withRouter';

// Use this component when you need a react-router <Link> wrapping
// a va-link web component from the Design System Library
// https://design.va.gov/storybook/?path=/docs/components-va-link--docs
const isModifiedEvent = event =>
  !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);

const BasicLink = ({
  path,
  search,
  navigate,
  onClick,
  replace,
  action,
  ...props
}) => {
  const ariaLabel = props?.label ?? props?.['aria-label'];
  const LinkComponent = action ? VaLinkAction : VaLink;

  // Get base URL
  const baseHref = useHref('/');
  const basePath = baseHref === '/' ? '' : baseHref;

  // Normalize path: add leading slash unless it's intentionally relative (../ or ./)
  const isRelative = path?.startsWith('../') || path?.startsWith('./');
  const normalizedPath =
    path && !isRelative && !path.startsWith('/') ? `/${path}` : path;
  const isCanonicalTrackClaimsPath =
    normalizedPath === '/track-claims' ||
    normalizedPath?.startsWith('/track-claims/');

  // Remove basename from path if it's already included, to avoid double-prepending
  let pathToResolve = normalizedPath;

  if (isCanonicalTrackClaimsPath && normalizedPath) {
    pathToResolve = normalizedPath.replace('/track-claims', '') || '/';
  } else if (basePath && normalizedPath?.startsWith(basePath)) {
    pathToResolve = normalizedPath.replace(basePath, '') || '/';
  }

  const href = useHref(pathToResolve) + (search || '');
  const destination =
    search && pathToResolve ? `${pathToResolve}${search}` : pathToResolve;

  return (
    <LinkComponent
      {...props}
      href={href}
      label={ariaLabel}
      onClick={event => {
        if (onClick) {
          onClick(event);
        }

        if (event.defaultPrevented) {
          return;
        }

        // Only intercept unmodified
        // left-clicks targeting the current browsing context.
        if (event.button !== 0 || isModifiedEvent(event)) {
          return;
        }

        event.preventDefault();

        if (destination) {
          navigate(destination, { replace: !!replace });
        }
      }}
    />
  );
};

BasicLink.propTypes = {
  action: PropTypes.bool,
  'aria-label': PropTypes.string,
  label: PropTypes.string,
  navigate: PropTypes.func,
  path: PropTypes.string,
  replace: PropTypes.bool,
  search: PropTypes.string,
  target: PropTypes.string,
  type: PropTypes.string,
  onClick: PropTypes.func,
};

export default withRouter(BasicLink);
